import React, { useState, useMemo, useEffect } from "react";
import useAuth from "../../../../hooks/useAuth";
import {
  getSimpleProductRankPaymentAPI,
  getSimpleAdAPI,
  createSimpleProductKeywordAPI,
  deleteSimpleProductAPI,
  deleteSimpleProductKeywordAPI,
  refreshSimpleProductRankAPI,
  getHotKeywordAPI,
  getKeywordBlogCountAPI,
  cancelSimpleProductRankPaymentAPI,
} from "../../../../api/index";
import "./RankKeyword.css";
import SimpleKeywordRankHistoryModal from "./SimpleKeywordRankHistoryModal";
import SimpleProductRegisterModal, {
  extractNaverProductIdFromUrl,
} from "./SimpleProductRegisterModal";
import SimpleRankPlanUpgradeModal from "./SimpleRankPlanUpgradeModal";

const HOT_PAGE_SIZE = 20;
const RELATED_KEYWORDS_MAX = 100;

const MAIN_TAB = {
  AD_RANK: "adRank",
  HOT_KEYWORD: "hotKeyword",
};

const DEFAULT_AD_LIMIT_PRODUCT = 5;
const DEFAULT_AD_LIMIT_KEYWORD = 5;

// 서버(ProductRankingCrudService.REFRESH_COOLDOWN_MS)와 동일한 값
const REFRESH_COOLDOWN_MS = 5 * 60 * 1000;

const getProductLastRefreshedAt = (product) => {
  const keywords = Array.isArray(product?.keywords) ? product.keywords : [];
  return keywords.reduce((max, kw) => {
    const t = kw?.recent_date ? new Date(kw.recent_date).getTime() : 0;
    return Number.isFinite(t) && t > max ? t : max;
  }, 0);
};

const formatCooldownRemaining = (ms) => {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return min > 0 ? `${min}분 ${sec}초` : `${sec}초`;
};

const parsePaymentLimits = (paymentResponseData) => {
  const items = paymentResponseData?.items ?? [];
  const active = items.find((row) => row && row.active_status === true);
  if (!active) {
    return {
      limitProduct: DEFAULT_AD_LIMIT_PRODUCT,
      limitKeyword: DEFAULT_AD_LIMIT_KEYWORD,
      activePayment: null,
    };
  }
  const lp = active.limit_product;
  const lk = active.limit_keyword;
  return {
    limitProduct:
      typeof lp === "number" && Number.isFinite(lp) && lp >= 0
        ? lp
        : DEFAULT_AD_LIMIT_PRODUCT,
    limitKeyword:
      typeof lk === "number" && Number.isFinite(lk) && lk >= 0
        ? lk
        : DEFAULT_AD_LIMIT_KEYWORD,
    activePayment: active,
  };
};

const formatActiveSubscribe = (activePayment) => {
  if (!activePayment) return "-";
  const raw =
    activePayment.subscribe_allow ??
    activePayment.subscribe ??
    activePayment.is_subscribe;
  if (raw === true || raw === "true" || raw === 1) return "예";
  if (raw === false || raw === "false" || raw === 0) return "아니오";
  return "-";
};

const isSubscribeActive = (activePayment) =>
  formatActiveSubscribe(activePayment) === "예";

const memberPaymentRowId = (activePayment) => {
  if (!activePayment) return null;
  const id = activePayment.id ?? activePayment._id;
  if (id === undefined || id === null) return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : id;
};

/** 검색량·플랜기간 등 주석 해제 UI용 — 현재 화면 비노출 */
const formatDateTimeKo = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
};

const isKeywordPaused = (kw) => kw?.active_status === false;

/** 검색량 컬럼 주석 해제 시 사용 */
const formatSearchVolume = (sv) => {
  if (!sv || !Number.isFinite(Number(sv.total_qc))) return "-";
  const n = Number(sv.total_qc).toLocaleString("ko-KR");
  return sv.is_low ? `${n} 미만` : n;
};

// 주석 처리된 검색량 UI 복구 시 사용하기 위해 바인딩 유지
void formatDateTimeKo;
void formatSearchVolume;

const sameEntityId = (a, b) =>
  a === b || (a != null && b != null && String(a) === String(b));

const formatRank = (rank) => {
  if (rank === 999 || rank === undefined || rank === null) return "500위 밖";
  if (rank === 0) return "0위";
  return `${rank}위`;
};

const parseNumber = (value) => {
  if (value === undefined || value === null) return 0;
  if (typeof value === "string" && value.includes("<")) {
    const num = parseInt(value.replace("<", "").trim(), 10);
    return isNaN(num) ? 0 : num;
  }
  const num = typeof value === "number" ? value : parseFloat(value);
  return isNaN(num) ? 0 : num;
};

const formatValue = (value) => {
  if (value === undefined || value === null) return "-";
  if (typeof value === "string") return value;
  if (typeof value === "number") {
    return value % 1 === 0 ? value : value.toFixed(2);
  }
  return value;
};

const totalSearchQc = (keyword) =>
  parseNumber(keyword.monthlyPcQcCnt) + parseNumber(keyword.monthlyMobileQcCnt);

const formatBlogSaturation = (blogTotal, keywordRow) => {
  if (blogTotal === undefined || blogTotal === null) return "-";
  const qc = totalSearchQc(keywordRow);
  if (!qc) return "-";
  const pct = (blogTotal / qc) * 100;
  if (!Number.isFinite(pct)) return "-";
  return `${pct >= 100 ? pct.toFixed(1) : pct.toFixed(2)}%`;
};

const RankKeyword = ({ variant = "rankCheck" }) => {
  const { user } = useAuth();
  const mainTab =
    variant === "keywordAnalysis" ? MAIN_TAB.HOT_KEYWORD : MAIN_TAB.AD_RANK;
  const isRankCheck = mainTab === MAIN_TAB.AD_RANK;

  const [adRankLoading, setAdRankLoading] = useState(false);
  const [adRankError, setAdRankError] = useState(null);
  const [adPaymentLimits, setAdPaymentLimits] = useState({
    limitProduct: DEFAULT_AD_LIMIT_PRODUCT,
    limitKeyword: DEFAULT_AD_LIMIT_KEYWORD,
    activePayment: null,
  });
  const [simpleAdProducts, setSimpleAdProducts] = useState([]);
  const [volumeLocked, setVolumeLocked] = useState(true);
  const [deletingSimpleProductId, setDeletingSimpleProductId] = useState(null);
  const [deletingSimpleKeywordId, setDeletingSimpleKeywordId] = useState(null);
  const [refreshingSimpleProductId, setRefreshingSimpleProductId] =
    useState(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [simpleKeywordHistoryModal, setSimpleKeywordHistoryModal] =
    useState(null);

  const [productRegisterModalOpen, setProductRegisterModalOpen] =
    useState(false);
  const [planUpgradeModalOpen, setPlanUpgradeModalOpen] = useState(false);
  const [keywordInputByProductId, setKeywordInputByProductId] = useState({});
  const [addingKeywordForProductId, setAddingKeywordForProductId] =
    useState(null);

  const [hotSeed, setHotSeed] = useState("");
  const [baseKeywords, setBaseKeywords] = useState([]);
  const [relatedKeywords, setRelatedKeywords] = useState([]);
  const [keywordFilter, setKeywordFilter] = useState("");
  const [sortConfig, setSortConfig] = useState({ field: null, order: null });
  const [hotLoading, setHotLoading] = useState(false);
  const [hotError, setHotError] = useState(null);
  const [hotSearched, setHotSearched] = useState(false);
  const [hotPage, setHotPage] = useState(1);
  const [blogCountByKeyword, setBlogCountByKeyword] = useState({});
  const [blogCountLoading, setBlogCountLoading] = useState(false);
  const [cancelingSubscribe, setCancelingSubscribe] = useState(false);

  const filteredAndSortedKeywords = useMemo(() => {
    const allKeywords = [...baseKeywords, ...relatedKeywords];

    let filtered = allKeywords.filter((keyword) => {
      if (
        keywordFilter &&
        !keyword.relKeyword?.toLowerCase().includes(keywordFilter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });

    if (sortConfig.field && sortConfig.order) {
      filtered = [...filtered].sort((a, b) => {
        let aValue;
        let bValue;

        switch (sortConfig.field) {
          case "relKeyword":
            aValue = a.relKeyword || "";
            bValue = b.relKeyword || "";
            return sortConfig.order === "asc"
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          case "monthlyPcQcCnt":
            aValue = parseNumber(a.monthlyPcQcCnt);
            bValue = parseNumber(b.monthlyPcQcCnt);
            break;
          case "monthlyMobileQcCnt":
            aValue = parseNumber(a.monthlyMobileQcCnt);
            bValue = parseNumber(b.monthlyMobileQcCnt);
            break;
          case "totalQcCnt":
            aValue =
              parseNumber(a.monthlyPcQcCnt) + parseNumber(a.monthlyMobileQcCnt);
            bValue =
              parseNumber(b.monthlyPcQcCnt) + parseNumber(b.monthlyMobileQcCnt);
            break;
          case "monthlyAvePcClkCnt":
            aValue = parseNumber(a.monthlyAvePcClkCnt);
            bValue = parseNumber(b.monthlyAvePcClkCnt);
            break;
          case "monthlyAveMobileClkCnt":
            aValue = parseNumber(a.monthlyAveMobileClkCnt);
            bValue = parseNumber(b.monthlyAveMobileClkCnt);
            break;
          case "monthlyAvePcCtr":
            aValue = parseNumber(a.monthlyAvePcCtr);
            bValue = parseNumber(b.monthlyAvePcCtr);
            break;
          case "monthlyAveMobileCtr":
            aValue = parseNumber(a.monthlyAveMobileCtr);
            bValue = parseNumber(b.monthlyAveMobileCtr);
            break;
          case "plAvgDepth":
            aValue = parseNumber(a.plAvgDepth);
            bValue = parseNumber(b.plAvgDepth);
            break;
          case "compIdx": {
            const compOrder = { 높음: 3, 중간: 2, 낮음: 1 };
            aValue = compOrder[a.compIdx] || 0;
            bValue = compOrder[b.compIdx] || 0;
            break;
          }
          default:
            return 0;
        }

        if (sortConfig.order === "asc") {
          return aValue - bValue;
        }
        return bValue - aValue;
      });
    }

    return filtered;
  }, [baseKeywords, relatedKeywords, keywordFilter, sortConfig]);

  const hotTotalCount = filteredAndSortedKeywords.length;
  const hotTotalPages =
    hotTotalCount === 0 ? 0 : Math.ceil(hotTotalCount / HOT_PAGE_SIZE);

  const paginatedHotKeywords = useMemo(() => {
    const start = (hotPage - 1) * HOT_PAGE_SIZE;
    return filteredAndSortedKeywords.slice(start, start + HOT_PAGE_SIZE);
  }, [filteredAndSortedKeywords, hotPage]);

  useEffect(() => {
    if (user) return;
    setSimpleAdProducts([]);
    setVolumeLocked(true);
    setAdRankError(null);
    setAdRankLoading(false);
    setAdPaymentLimits({
      limitProduct: DEFAULT_AD_LIMIT_PRODUCT,
      limitKeyword: DEFAULT_AD_LIMIT_KEYWORD,
      activePayment: null,
    });
    setProductRegisterModalOpen(false);
    setPlanUpgradeModalOpen(false);
    setSimpleKeywordHistoryModal(null);
    setKeywordInputByProductId({});
    setAddingKeywordForProductId(null);
    setDeletingSimpleProductId(null);
    setDeletingSimpleKeywordId(null);
    setHotSeed("");
    setBaseKeywords([]);
    setRelatedKeywords([]);
    setKeywordFilter("");
    setSortConfig({ field: null, order: null });
    setHotSearched(false);
    setHotPage(1);
    setBlogCountByKeyword({});
    setHotError(null);
  }, [user]);

  useEffect(() => {
    if (hotTotalPages === 0) {
      setHotPage(1);
      return;
    }
    setHotPage((p) => Math.min(Math.max(1, p), hotTotalPages));
  }, [hotTotalPages]);

  useEffect(() => {
    setHotPage(1);
  }, [sortConfig]);

  useEffect(() => {
    if (!user || mainTab !== MAIN_TAB.HOT_KEYWORD) return;
    if (!paginatedHotKeywords.length) {
      setBlogCountLoading(false);
      return;
    }

    const parts = paginatedHotKeywords
      .map((k) => k.relKeyword)
      .filter((s) => s != null && String(s).trim() !== "");
    if (!parts.length) {
      setBlogCountLoading(false);
      return;
    }

    const missing = parts.filter(
      (k) =>
        !Object.prototype.hasOwnProperty.call(blogCountByKeyword, String(k)),
    );
    if (!missing.length) {
      setBlogCountLoading(false);
      return;
    }

    let cancelled = false;
    setBlogCountLoading(true);

    getKeywordBlogCountAPI(missing.join(","))
      .then((res) => {
        if (cancelled) return;
        const body = res.data;
        const list = body?.list ?? body?.data?.list ?? [];
        const patch = {};
        for (const item of list) {
          if (item && item.keyword != null) {
            patch[String(item.keyword)] = item.total;
          }
        }
        setBlogCountByKeyword((p) => ({ ...p, ...patch }));
      })
      .catch((err) => {
        console.error("블로그 발행 수 조회 실패:", err);
      })
      .finally(() => {
        if (!cancelled) setBlogCountLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user, mainTab, paginatedHotKeywords, blogCountByKeyword]);

  const simpleAdUsage = useMemo(() => {
    let keywordCount = 0;
    let pausedByExpiryCount = 0;
    // 서버 한도 검사와 동일하게 활성 키워드만 센다 (플랜 만료 시 초과분은 비활성 처리됨)
    for (const p of simpleAdProducts) {
      const kws = Array.isArray(p.keywords) ? p.keywords : [];
      keywordCount += kws.filter((k) => !isKeywordPaused(k)).length;
      pausedByExpiryCount += kws.filter(
        (k) => isKeywordPaused(k) && k.pause_reason === "plan_expired",
      ).length;
    }
    return {
      productCount: simpleAdProducts.length,
      keywordCount,
      pausedByExpiryCount,
    };
  }, [simpleAdProducts]);

  useEffect(() => {
    if (!user || mainTab !== MAIN_TAB.AD_RANK) return undefined;
    let cancelled = false;
    setAdRankLoading(true);
    setAdRankError(null);

    (async () => {
      const fallbackLimits = {
        limitProduct: DEFAULT_AD_LIMIT_PRODUCT,
        limitKeyword: DEFAULT_AD_LIMIT_KEYWORD,
        activePayment: null,
      };

      try {
        const payRes = await getSimpleProductRankPaymentAPI();
        if (cancelled) return;
        setAdPaymentLimits(parsePaymentLimits(payRes.data));
      } catch (err) {
        if (!cancelled) {
          console.error("간편 순위 결제 정보 조회 실패:", err);
          setAdPaymentLimits(fallbackLimits);
        }
      }

      if (cancelled) return;

      try {
        const prodRes = await getSimpleAdAPI();
        if (cancelled) return;
        setSimpleAdProducts(prodRes.data?.items ?? []);
        setVolumeLocked(prodRes.data?.volume_locked !== false);
      } catch (err) {
        if (!cancelled) {
          console.error("간편 광고 상품 조회 실패:", err);
          setSimpleAdProducts([]);
          setAdRankError(
            err.response?.data?.msg ||
              err.message ||
              "상품 목록을 불러오지 못했습니다.",
          );
        }
      } finally {
        if (!cancelled) setAdRankLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      setAdRankLoading(false);
    };
  }, [user, mainTab]);

  // 상품별 새로고침 쿨다운 카운트다운 표시용 (탭이 보일 때만 갱신)
  useEffect(() => {
    if (mainTab !== MAIN_TAB.AD_RANK) return undefined;
    const intervalId = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, [mainTab]);

  const refreshAdRankPaymentAndProducts = async () => {
    if (!user) return;
    const fallbackLimits = {
      limitProduct: DEFAULT_AD_LIMIT_PRODUCT,
      limitKeyword: DEFAULT_AD_LIMIT_KEYWORD,
      activePayment: null,
    };
    try {
      const payRes = await getSimpleProductRankPaymentAPI();
      setAdPaymentLimits(parsePaymentLimits(payRes.data));
    } catch (err) {
      console.error("간편 순위 결제 정보 조회 실패:", err);
      setAdPaymentLimits(fallbackLimits);
    }
    try {
      const prodRes = await getSimpleAdAPI();
      setSimpleAdProducts(prodRes.data?.items ?? []);
      setVolumeLocked(prodRes.data?.volume_locked !== false);
    } catch (err) {
      console.error("간편 광고 상품 조회 실패:", err);
    }
  };

  const refreshSimpleAdProducts = async () => {
    if (!user) return;
    const prodRes = await getSimpleAdAPI();
    setSimpleAdProducts(prodRes.data?.items ?? []);
    setVolumeLocked(prodRes.data?.volume_locked !== false);
  };

  const handleCancelSubscribeClick = async () => {
    if (!user) return;
    const ap = adPaymentLimits.activePayment;
    if (!ap || !isSubscribeActive(ap)) return;
    const id = memberPaymentRowId(ap);
    if (id === null) {
      window.alert("결제 정보 id를 찾을 수 없습니다.");
      return;
    }
    if (
      !window.confirm(
        "구독을 취소하시겠습니까? 취소 후에는 구독 혜택이 중단될 수 있습니다.",
      )
    ) {
      return;
    }
    setCancelingSubscribe(true);
    try {
      await cancelSimpleProductRankPaymentAPI({
        id,
        subscribe: false,
      });
      await refreshAdRankPaymentAndProducts();
    } catch (err) {
      window.alert(
        err.response?.data?.msg ||
          err.message ||
          "구독 취소 요청에 실패했습니다.",
      );
    } finally {
      setCancelingSubscribe(false);
    }
  };

  const handleAddSimpleKeyword = async (product) => {
    if (!user) {
      window.alert("로그인 후 이용할 수 있습니다.");
      return;
    }
    const pid = product?.id;
    if (pid === undefined || pid === null) {
      window.alert(
        "키워드를 추가할 수 없습니다. 상품 정보가 올바르지 않습니다.",
      );
      return;
    }
    const raw = String(keywordInputByProductId[pid] ?? "").trim();
    const keywords = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!keywords.length) {
      window.alert(
        "추가할 키워드를 입력해주세요. 여러 개는 쉼표(,)로 구분합니다.",
      );
      return;
    }
    const limitKw = Math.max(0, Number(adPaymentLimits.limitKeyword) || 0);
    const curKw = Math.max(0, simpleAdUsage.keywordCount);
    const remainingKw = Math.max(0, limitKw - curKw);
    if (keywords.length > remainingKw) {
      window.alert(
        limitKw === 0
          ? "키워드 한도를 확인할 수 없거나 등록할 수 없습니다."
          : `키워드는 총 ${limitKw}개까지만 등록할 수 있습니다. (현재 ${curKw}개 사용 중 → 최대 ${remainingKw}개까지 추가 가능, 이번에 ${keywords.length}개 입력)`,
      );
      return;
    }

    const productUrl = product.product_url || product.productUrl || "";
    const naverProductId =
      product.naver_product_id != null &&
      Number.isFinite(Number(product.naver_product_id))
        ? Number(product.naver_product_id)
        : extractNaverProductIdFromUrl(productUrl);
    if (naverProductId == null) {
      window.alert(
        "상품 URL에서 네이버 상품 번호(naver_product_id)를 찾을 수 없습니다. 스마트스토어 …/products/숫자 형식인지 확인해주세요.",
      );
      return;
    }

    setAddingKeywordForProductId(pid);
    try {
      await createSimpleProductKeywordAPI({
        product_id: Number.isFinite(Number(pid)) ? Number(pid) : pid,
        keywords,
        naver_product_id: naverProductId,
      });
      setKeywordInputByProductId((prev) => ({ ...prev, [pid]: "" }));
      await refreshSimpleAdProducts();
    } catch (err) {
      console.error("간편 키워드 등록 실패:", err);
      window.alert(
        err.response?.data?.msg || err.message || "키워드 등록에 실패했습니다.",
      );
    } finally {
      setAddingKeywordForProductId(null);
    }
  };

  const handleDeleteSimpleProduct = async (product) => {
    if (!user) {
      window.alert("로그인 후 이용할 수 있습니다.");
      return;
    }
    const id = product?.id;
    if (id === undefined || id === null) {
      window.alert("삭제할 수 없습니다. 상품 ID가 없습니다.");
      return;
    }
    const name = product.product_name || "이 상품";
    const ok = window.confirm(
      `「${name}」을(를) 삭제하시겠습니까?\n삭제 후에는 복구할 수 없습니다.`,
    );
    if (!ok) return;

    setDeletingSimpleProductId(id);
    try {
      await deleteSimpleProductAPI(id);
      setSimpleAdProducts((prev) =>
        prev.filter((p) => !sameEntityId(p.id, id)),
      );
    } catch (err) {
      console.error("간편 상품 삭제 실패:", err);
      window.alert(
        err.response?.data?.msg || err.message || "삭제에 실패했습니다.",
      );
    } finally {
      setDeletingSimpleProductId(null);
    }
  };

  const handleDeleteSimpleKeyword = async (product, kw) => {
    if (!user) {
      window.alert("로그인 후 이용할 수 있습니다.");
      return;
    }
    const keywordId = kw?.id;
    if (keywordId === undefined || keywordId === null) {
      window.alert("삭제할 수 없습니다. 키워드 ID가 없습니다.");
      return;
    }
    const word = kw.word || "이 키워드";
    const ok = window.confirm(
      `「${word}」키워드를 삭제하시겠습니까?\n삭제 후에는 복구할 수 없습니다.`,
    );
    if (!ok) return;

    const productId = product?.id;
    setDeletingSimpleKeywordId(keywordId);
    try {
      await deleteSimpleProductKeywordAPI(keywordId);
      setSimpleAdProducts((prev) =>
        prev.map((p) => {
          if (!sameEntityId(p.id, productId)) return p;
          const list = Array.isArray(p.keywords) ? p.keywords : [];
          return {
            ...p,
            keywords: list.filter((k) => !sameEntityId(k.id, keywordId)),
          };
        }),
      );
    } catch (err) {
      console.error("간편 키워드 삭제 실패:", err);
      window.alert(
        err.response?.data?.msg || err.message || "키워드 삭제에 실패했습니다.",
      );
    } finally {
      setDeletingSimpleKeywordId(null);
    }
  };

  const handleRefreshSimpleProduct = async (product) => {
    if (!user) {
      window.alert("로그인 후 이용할 수 있습니다.");
      return;
    }
    const productId = product?.id;
    if (productId === undefined || productId === null) {
      window.alert("새로고침할 수 없습니다. 상품 ID가 없습니다.");
      return;
    }
    setRefreshingSimpleProductId(productId);
    try {
      const response = await refreshSimpleProductRankAPI(productId);
      const updated = response.data?.keywords ?? [];
      const rankById = new Map(updated.map((k) => [k.id, k]));
      setSimpleAdProducts((prev) =>
        prev.map((p) => {
          if (!sameEntityId(p.id, productId)) return p;
          const list = Array.isArray(p.keywords) ? p.keywords : [];
          return {
            ...p,
            keywords: list.map((k) => {
              const fresh = rankById.get(k.id);
              return fresh
                ? { ...k, rank: fresh.rank, recent_date: fresh.recent_date }
                : k;
            }),
          };
        }),
      );
    } catch (err) {
      console.error("간편 상품 순위 새로고침 실패:", err);
      window.alert(
        err.response?.data?.msg ||
          err.message ||
          "순위 새로고침에 실패했습니다.",
      );
    } finally {
      setRefreshingSimpleProductId(null);
    }
  };

  const handleSort = (field) => {
    setSortConfig((prev) => {
      if (prev.field === field) {
        if (prev.order === "asc") {
          return { field, order: "desc" };
        }
        if (prev.order === "desc") {
          return { field: null, order: null };
        }
      }
      return { field, order: "asc" };
    });
  };

  const renderSortIcon = (field) => {
    if (sortConfig.field !== field) {
      return (
        <span className="rank-keyword-sort-icon rank-keyword-sort-neutral">
          ↕
        </span>
      );
    }
    if (sortConfig.order === "asc") {
      return (
        <span className="rank-keyword-sort-icon rank-keyword-sort-active">
          ↑
        </span>
      );
    }
    return (
      <span className="rank-keyword-sort-icon rank-keyword-sort-active">↓</span>
    );
  };

  const handleHotKeywordSearch = async () => {
    if (!user) {
      setHotError("로그인 후 키워드 검색을 이용할 수 있습니다.");
      return;
    }
    const seed = hotSeed.trim();
    setHotError(null);
    setBlogCountByKeyword({});
    setBaseKeywords([]);
    setRelatedKeywords([]);
    setKeywordFilter("");
    setSortConfig({ field: null, order: null });
    setHotSearched(false);
    setHotPage(1);

    if (!seed) {
      setHotError("검색할 키워드를 입력해주세요.");
      return;
    }

    setHotLoading(true);
    try {
      const response = await getHotKeywordAPI(seed);
      if (response.data?.success && response.data?.data) {
        const baseKeywordsData = response.data.data.baseKeywords || [];
        setBaseKeywords(baseKeywordsData);

        const relatedKeywordsData = response.data.data.relatedKeywords || [];
        const sortedRelatedKeywords = [...relatedKeywordsData]
          .sort((a, b) => {
            const aTotal =
              parseNumber(a.monthlyMobileQcCnt) +
              parseNumber(a.monthlyAvePcClkCnt);
            const bTotal =
              parseNumber(b.monthlyMobileQcCnt) +
              parseNumber(b.monthlyAvePcClkCnt);
            return bTotal - aTotal;
          })
          .slice(0, RELATED_KEYWORDS_MAX);
        setRelatedKeywords(sortedRelatedKeywords);
        setHotSearched(true);

        if (
          baseKeywordsData.length === 0 &&
          sortedRelatedKeywords.length === 0
        ) {
          window.alert("검색 결과를 찾을 수 없습니다.");
        }
      } else {
        window.alert("검색 결과를 찾을 수 없습니다.");
      }
    } catch (err) {
      console.error("키워드 검색 실패:", err);
      window.alert("키워드 검색에 실패했습니다.");
      setHotError(
        err.response?.data?.msg ||
          err.message ||
          "키워드 정보를 불러오지 못했습니다.",
      );
    } finally {
      setHotLoading(false);
    }
  };

  const compBadgeClass = (compIdx) => {
    if (compIdx === "높음") return "rank-keyword-comp rank-keyword-comp-high";
    if (compIdx === "중간") return "rank-keyword-comp rank-keyword-comp-mid";
    return "rank-keyword-comp rank-keyword-comp-low";
  };

  return (
    <div
      className={`rank-keyword-container${
        isRankCheck ? " is-rank-check" : " is-keyword-analysis"
      }`}
    >
      {!user && (
        <div className="rank-keyword-login-banner" role="status">
          로그인 후 실제 데이터 조회·등록이 가능합니다. 아래는 화면 구성
          미리보기입니다.
        </div>
      )}

      <div
        className="rank-keyword-tab-panel"
        role="tabpanel"
        hidden={!isRankCheck}
      >
        <div className="rank-keyword-sections">
          <section className="rank-keyword-card rank-keyword-simple-ad-card">
            <div className="rank-keyword-simple-ad-card-title-row">
              <h1 className="rank-keyword-page-title">상품 순위 확인</h1>
              {/* TODO: 결제 활성 */}
              {user && !adRankLoading && !adPaymentLimits.activePayment && (
                <button
                  type="button"
                  className="rank-keyword-simple-ad-upgrade-btn"
                  onClick={() => {
                    window.alert("출시 예정입니다.");
                  }}
                >
                  업그레이드
                </button>
              )}
            </div>

            <div className="rank-keyword-simple-ad-body">
            {adRankLoading && (
              <div className="rank-keyword-hot-loading rank-keyword-simple-ad-loading">
                <div className="rank-keyword-spinner" aria-hidden />
                <p>정보를 불러오는 중입니다…</p>
              </div>
            )}

            {!adRankLoading && (
              <>
                <div
                  className="rank-keyword-simple-ad-summary"
                  role="group"
                  aria-label="플랜·한도 요약"
                >
                  <div className="rank-keyword-simple-ad-summary-stat">
                    <span className="rank-keyword-simple-ad-summary-label">
                      플랜 상태
                    </span>
                    <span className="rank-keyword-simple-ad-summary-plan">
                      <span
                        className={
                          adPaymentLimits.activePayment
                            ? "rank-keyword-simple-ad-badge rank-keyword-simple-ad-badge-on"
                            : "rank-keyword-simple-ad-badge rank-keyword-simple-ad-badge-off"
                        }
                      >
                        {adPaymentLimits.activePayment ? "이용 중" : "비활성"}
                      </span>
                      {/* TODO: 결제 활성 */}
                      {adPaymentLimits.activePayment &&
                        isSubscribeActive(adPaymentLimits.activePayment) && (
                          <button
                            type="button"
                            className="rank-keyword-subscribe-cancel-btn"
                            onClick={handleCancelSubscribeClick}
                            disabled={cancelingSubscribe}
                          >
                            {cancelingSubscribe ? "처리 중…" : "구독 취소"}
                          </button>
                        )}
                    </span>
                  </div>
                  <div className="rank-keyword-simple-ad-summary-stat">
                    <span className="rank-keyword-simple-ad-summary-label">
                      상품 한도
                    </span>
                    <p className="rank-keyword-simple-ad-summary-metric">
                      <strong>{simpleAdUsage.productCount}</strong>
                      <span>/ {adPaymentLimits.limitProduct}</span>
                    </p>
                  </div>
                  <div className="rank-keyword-simple-ad-summary-stat">
                    <span className="rank-keyword-simple-ad-summary-label">
                      키워드 한도
                    </span>
                    <p className="rank-keyword-simple-ad-summary-metric">
                      <strong>{simpleAdUsage.keywordCount}</strong>
                      <span>/ {adPaymentLimits.limitKeyword}</span>
                    </p>
                  </div>
                </div>

                {simpleAdUsage.pausedByExpiryCount > 0 && (
                  <div className="rank-keyword-simple-ad-paused-banner">
                    플랜이 만료되어 키워드 {simpleAdUsage.pausedByExpiryCount}
                    개의 순위·검색량 수집이 일시정지되었습니다. 플랜을 구매하면
                    한도 안에서 자동으로 재개됩니다.
                  </div>
                )}

                <div className="rank-keyword-simple-ad-add-wrap">
                  <button
                    type="button"
                    className="rank-keyword-simple-ad-add-product-btn"
                    onClick={() => setProductRegisterModalOpen(true)}
                    disabled={
                      !user ||
                      simpleAdUsage.productCount >=
                        adPaymentLimits.limitProduct ||
                      simpleAdUsage.keywordCount >=
                        adPaymentLimits.limitKeyword ||
                      deletingSimpleProductId != null ||
                      deletingSimpleKeywordId != null
                    }
                  >
                    + 상품 추가
                  </button>
                  {simpleAdUsage.keywordCount >=
                    adPaymentLimits.limitKeyword && (
                    <p className="rank-keyword-simple-ad-add-hint">
                      키워드 한도에 도달했습니다. 키워드 추가·상품 추가를 할 수
                      없습니다.
                    </p>
                  )}
                  {simpleAdUsage.productCount >= adPaymentLimits.limitProduct &&
                    simpleAdUsage.keywordCount <
                      adPaymentLimits.limitKeyword && (
                      <p className="rank-keyword-simple-ad-add-hint">
                        등록 가능한 상품 수를 모두 사용했습니다.
                      </p>
                    )}
                </div>

                {adRankError && (
                  <div className="rank-keyword-error">{adRankError}</div>
                )}

                {!adRankError && simpleAdProducts.length === 0 && (
                  <div className="rank-keyword-hot-empty rank-keyword-simple-ad-empty">
                    <p>
                      {user
                        ? "등록된 상품이 없습니다."
                        : "로그인 후 등록한 상품·순위가 여기에 표시됩니다."}
                    </p>
                  </div>
                )}

                {!adRankError && simpleAdProducts.length > 0 && (
                  <div className="rank-keyword-simple-ad-product-grid">
                    {simpleAdProducts.map((product) => {
                      const lastRefreshedAt =
                        getProductLastRefreshedAt(product);
                      const cooldownRemainingMs = lastRefreshedAt
                        ? REFRESH_COOLDOWN_MS - (nowTick - lastRefreshedAt)
                        : 0;
                      const isCoolingDown = cooldownRemainingMs > 0;
                      const isRefreshingThis = sameEntityId(
                        refreshingSimpleProductId,
                        product.id,
                      );
                      const hasActiveKeyword = (product.keywords ?? []).some(
                        (k) => !isKeywordPaused(k),
                      );
                      const refreshDisabled =
                        !user ||
                        !hasActiveKeyword ||
                        isCoolingDown ||
                        refreshingSimpleProductId != null ||
                        deletingSimpleProductId != null ||
                        deletingSimpleKeywordId != null;
                      const refreshLabel = isRefreshingThis
                        ? "새로고침 중…"
                        : !hasActiveKeyword
                          ? "활성 키워드 없음"
                          : isCoolingDown
                            ? `${formatCooldownRemaining(cooldownRemainingMs)} 후 가능`
                            : "순위 새로고침";
                      const atKeywordLimit =
                        simpleAdUsage.keywordCount >=
                        adPaymentLimits.limitKeyword;
                      const kwInputDisabled =
                        !user ||
                        addingKeywordForProductId != null ||
                        atKeywordLimit ||
                        deletingSimpleProductId != null ||
                        deletingSimpleKeywordId != null;

                      return (
                        <article
                          key={product._id ?? product.id}
                          className="rank-keyword-simple-ad-product-card"
                        >
                          <header className="rank-keyword-simple-ad-product-head">
                            <div className="rank-keyword-simple-ad-product-identity">
                              {product.image_url ? (
                                <a
                                  href={product.product_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="rank-keyword-simple-ad-thumb-wrap"
                                >
                                  <img
                                    src={product.image_url}
                                    alt=""
                                    className="rank-keyword-simple-ad-thumb"
                                  />
                                </a>
                              ) : (
                                <div className="rank-keyword-simple-ad-thumb-placeholder" />
                              )}
                              <div className="rank-keyword-simple-ad-product-meta">
                                {product.store_name && (
                                  <p className="rank-keyword-simple-ad-store">
                                    {product.store_name}
                                  </p>
                                )}
                                <h4 className="rank-keyword-simple-ad-product-name">
                                  {product.product_url ? (
                                    <a
                                      href={product.product_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="rank-keyword-simple-ad-product-name-link"
                                    >
                                      {product.product_name || "상품"}
                                    </a>
                                  ) : (
                                    product.product_name || "상품"
                                  )}
                                </h4>
                              </div>
                            </div>
                            <div className="rank-keyword-simple-ad-product-actions">
                              <button
                                type="button"
                                className="rank-keyword-simple-ad-refresh-btn"
                                onClick={() =>
                                  handleRefreshSimpleProduct(product)
                                }
                                disabled={refreshDisabled}
                                title="이 상품의 키워드 순위 전체 새로고침 (상품별 5분에 1번)"
                              >
                                {refreshLabel}
                              </button>
                              <button
                                type="button"
                                className="rank-keyword-simple-ad-delete-btn"
                                onClick={() =>
                                  handleDeleteSimpleProduct(product)
                                }
                                disabled={
                                  !user ||
                                  deletingSimpleProductId != null ||
                                  deletingSimpleKeywordId != null
                                }
                              >
                                {sameEntityId(
                                  deletingSimpleProductId,
                                  product.id,
                                )
                                  ? "삭제 중…"
                                  : "삭제"}
                              </button>
                            </div>
                          </header>

                          <section
                            className="rank-keyword-simple-ad-kw-section"
                            data-volume-locked={volumeLocked ? "1" : "0"}
                          >
                            <div
                              className={`rank-keyword-simple-ad-search-field${
                                kwInputDisabled ? " is-disabled" : ""
                              }`}
                            >
                              <input
                                type="text"
                                className="rank-keyword-simple-ad-search-input"
                                placeholder="키워드 (여러 개는 쉼표로 구분)"
                                value={
                                  keywordInputByProductId[product.id] ?? ""
                                }
                                onChange={(e) =>
                                  setKeywordInputByProductId((prev) => ({
                                    ...prev,
                                    [product.id]: e.target.value,
                                  }))
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleAddSimpleKeyword(product);
                                  }
                                }}
                                disabled={kwInputDisabled}
                                aria-label="키워드 입력"
                              />
                              <button
                                type="button"
                                className="rank-keyword-simple-ad-search-submit"
                                onClick={() => handleAddSimpleKeyword(product)}
                                disabled={kwInputDisabled}
                              >
                                {sameEntityId(
                                  addingKeywordForProductId,
                                  product.id,
                                )
                                  ? "추가 중…"
                                  : "키워드 추가"}
                              </button>
                            </div>
                            {atKeywordLimit && (
                              <p className="rank-keyword-simple-ad-kw-add-hint">
                                등록 가능한 키워드 수를 모두 사용했습니다.
                              </p>
                            )}

                            {Array.isArray(product.keywords) &&
                            product.keywords.length > 0 ? (
                              <div className="rank-keyword-simple-ad-kw-table">
                                <div
                                  className="rank-keyword-simple-ad-kw-table-head"
                                  aria-hidden
                                >
                                  <span>키워드</span>
                                  {/* 검색량: 우선 비노출 — 배포 시 주석 유지
                                  <span>검색량</span>
                                  */}
                                  <span>순위</span>
                                  <span className="rank-keyword-simple-ad-kw-table-head-action">
                                    삭제
                                  </span>
                                </div>
                                <ul className="rank-keyword-simple-ad-kw-list">
                                  {product.keywords.map((kw) => (
                                    <li
                                      key={kw._id ?? kw.id}
                                      className={`rank-keyword-simple-ad-kw-row${
                                        isKeywordPaused(kw) ? " is-paused" : ""
                                      }`}
                                    >
                                      <span className="rank-keyword-simple-ad-kw-word">
                                        {kw.word ?? "-"}
                                      </span>
                                      {/* 검색량: 우선 비노출 — 배포 시 주석 유지
                                      <span
                                        className="rank-keyword-simple-ad-kw-volume-cell"
                                        title={
                                          volumeLocked
                                            ? "플랜 이용 중에만 검색량을 확인할 수 있습니다."
                                            : kw.search_volume
                                              ? `PC ${Number(kw.search_volume.pc_qc).toLocaleString("ko-KR")} · 모바일 ${Number(kw.search_volume.mobile_qc).toLocaleString("ko-KR")} (${kw.search_volume.date} 기준)`
                                              : "매일 오전 6시에 수집됩니다."
                                        }
                                      >
                                        {volumeLocked ? (
                                          <span className="rank-keyword-simple-ad-kw-volume-locked">
                                            🔒 플랜 전용
                                          </span>
                                        ) : kw.search_volume ? (
                                          formatSearchVolume(kw.search_volume)
                                        ) : (
                                          "수집 대기"
                                        )}
                                      </span>
                                      */}
                                      <button
                                        type="button"
                                        className="rank-keyword-simple-ad-kw-rank rank-keyword-simple-ad-kw-rank-btn"
                                        onClick={() => {
                                          if (!user) {
                                            window.alert(
                                              "로그인 후 이용할 수 있습니다.",
                                            );
                                            return;
                                          }
                                          if (kw.id == null) {
                                            window.alert(
                                              "순위 기록을 불러올 수 없습니다. 키워드 ID가 없습니다.",
                                            );
                                            return;
                                          }
                                          setSimpleKeywordHistoryModal({
                                            keywordId: kw.id,
                                            word: kw.word ?? "",
                                          });
                                        }}
                                      >
                                        {formatRank(kw.rank)}
                                      </button>
                                      <button
                                        type="button"
                                        className="rank-keyword-simple-ad-kw-delete-btn"
                                        onClick={() =>
                                          handleDeleteSimpleKeyword(
                                            product,
                                            kw,
                                          )
                                        }
                                        disabled={
                                          !user ||
                                          deletingSimpleKeywordId != null ||
                                          deletingSimpleProductId != null
                                        }
                                      >
                                        {sameEntityId(
                                          deletingSimpleKeywordId,
                                          kw.id,
                                        )
                                          ? "삭제 중…"
                                          : "삭제"}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : (
                              <p className="rank-keyword-simple-ad-no-kw">
                                등록된 키워드가 없습니다.
                              </p>
                            )}
                          </section>
                        </article>
                      );
                    })}
                  </div>
                )}
              </>
            )}
            </div>
          </section>
        </div>
      </div>

      <div
        className="rank-keyword-tab-panel"
        role="tabpanel"
        hidden={mainTab !== MAIN_TAB.HOT_KEYWORD}
      >
        <section className="rank-keyword-card rank-keyword-hot-card">
          <div className="rank-keyword-hot-title-row">
            <h1 className="rank-keyword-page-title">키워드 분석</h1>
          </div>

          <div className="rank-keyword-hot-body">
            <div className="rank-keyword-hot-search-block">
              <p className="rank-keyword-hot-search-desc">
                검색량·경쟁도·연관 키워드·블로그 포화도를 한눈에 확인합니다.
              </p>
              <div
                className={`rank-keyword-hot-search-field${!user ? " is-disabled" : ""}`}
              >
                <input
                  id="rk-hot-seed"
                  type="text"
                  className="rank-keyword-hot-search-input"
                  placeholder="검색하고 싶은 키워드를 입력하세요"
                  value={hotSeed}
                  onChange={(e) => setHotSeed(e.target.value)}
                  disabled={!user}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleHotKeywordSearch();
                    }
                  }}
                  aria-label="키워드 검색"
                />
                <button
                  type="button"
                  className="rank-keyword-hot-search-submit"
                  onClick={handleHotKeywordSearch}
                  disabled={hotLoading || !user}
                >
                  {hotLoading ? "검색 중…" : "키워드 검색"}
                </button>
              </div>
            </div>

            {hotError && <div className="rank-keyword-error">{hotError}</div>}

            {hotLoading && (
              <div className="rank-keyword-hot-loading">
                <div className="rank-keyword-spinner" aria-hidden />
                <p>키워드를 검색하고 있습니다…</p>
              </div>
            )}

            {!hotLoading &&
              (baseKeywords.length > 0 || relatedKeywords.length > 0) && (
                <div className="rank-keyword-hot-table-card">
                  <div className="rank-keyword-hot-table-toolbar">
                    <div className="rank-keyword-hot-table-meta">
                      <h2 className="rank-keyword-hot-table-title">검색 결과</h2>
                      <span className="rank-keyword-hot-table-count">
                        {filteredAndSortedKeywords.length}
                        <em>
                          {" "}
                          / {baseKeywords.length + relatedKeywords.length}개
                        </em>
                      </span>
                      {blogCountLoading && (
                        <span className="rank-keyword-hot-table-hint">
                          발행 수 불러오는 중…
                        </span>
                      )}
                    </div>
                    <div className="rank-keyword-hot-filter-field">
                      <input
                        id="rk-hot-filter"
                        type="text"
                        placeholder="결과 내 키워드 필터"
                        value={keywordFilter}
                        onChange={(e) => {
                          setKeywordFilter(e.target.value);
                          setHotPage(1);
                        }}
                        className="rank-keyword-hot-filter-input"
                        aria-label="결과 내 키워드 필터"
                      />
                      {keywordFilter && (
                        <button
                          type="button"
                          className="rank-keyword-hot-filter-clear"
                          onClick={() => {
                            setKeywordFilter("");
                            setHotPage(1);
                          }}
                          title="검색어 지우기"
                          aria-label="필터 지우기"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>

                  {filteredAndSortedKeywords.length === 0 ? (
                    <div className="rank-keyword-hot-empty rank-keyword-hot-empty-inline">
                      <p>필터 조건에 맞는 검색 결과가 없습니다.</p>
                      <p>필터를 조정해보세요.</p>
                    </div>
                  ) : (
                    <>
                      <div className="rank-keyword-table-wrap">
                        <table className="rank-keyword-table">
                          <thead>
                            <tr>
                              <th
                                scope="col"
                                className="rank-keyword-th-sort rank-keyword-th-sticky"
                                onClick={() => handleSort("relKeyword")}
                              >
                                <span className="rank-keyword-th-inner">
                                  키워드 {renderSortIcon("relKeyword")}
                                </span>
                              </th>
                              <th
                                scope="col"
                                className="rank-keyword-th-sort rank-keyword-num"
                                onClick={() => handleSort("totalQcCnt")}
                              >
                                <span className="rank-keyword-th-inner">
                                  검색수 합계 {renderSortIcon("totalQcCnt")}
                                </span>
                              </th>
                              <th
                                scope="col"
                                className="rank-keyword-th-sort rank-keyword-num"
                                onClick={() => handleSort("monthlyPcQcCnt")}
                              >
                                <span className="rank-keyword-th-inner">
                                  PC 검색량 {renderSortIcon("monthlyPcQcCnt")}
                                </span>
                              </th>
                              <th
                                scope="col"
                                className="rank-keyword-th-sort rank-keyword-num"
                                onClick={() => handleSort("monthlyMobileQcCnt")}
                              >
                                <span className="rank-keyword-th-inner">
                                  모바일 검색량{" "}
                                  {renderSortIcon("monthlyMobileQcCnt")}
                                </span>
                              </th>
                              <th
                                scope="col"
                                className="rank-keyword-th-sort rank-keyword-num"
                                onClick={() => handleSort("monthlyAvePcClkCnt")}
                              >
                                <span className="rank-keyword-th-inner">
                                  PC 클릭수{" "}
                                  {renderSortIcon("monthlyAvePcClkCnt")}
                                </span>
                              </th>
                              <th
                                scope="col"
                                className="rank-keyword-th-sort rank-keyword-num"
                                onClick={() =>
                                  handleSort("monthlyAveMobileClkCnt")
                                }
                              >
                                <span className="rank-keyword-th-inner">
                                  모바일 클릭수{" "}
                                  {renderSortIcon("monthlyAveMobileClkCnt")}
                                </span>
                              </th>
                              <th
                                scope="col"
                                className="rank-keyword-th-sort rank-keyword-num"
                                onClick={() => handleSort("monthlyAvePcCtr")}
                              >
                                <span className="rank-keyword-th-inner">
                                  PC CTR {renderSortIcon("monthlyAvePcCtr")}
                                </span>
                              </th>
                              <th
                                scope="col"
                                className="rank-keyword-th-sort rank-keyword-num"
                                onClick={() =>
                                  handleSort("monthlyAveMobileCtr")
                                }
                              >
                                <span className="rank-keyword-th-inner">
                                  모바일 CTR{" "}
                                  {renderSortIcon("monthlyAveMobileCtr")}
                                </span>
                              </th>
                              <th
                                scope="col"
                                className="rank-keyword-th-sort rank-keyword-num"
                                onClick={() => handleSort("plAvgDepth")}
                              >
                                <span className="rank-keyword-th-inner">
                                  평균 깊이 {renderSortIcon("plAvgDepth")}
                                </span>
                              </th>
                              <th
                                scope="col"
                                className="rank-keyword-th-sort rank-keyword-comp-cell"
                                onClick={() => handleSort("compIdx")}
                              >
                                <span className="rank-keyword-th-inner">
                                  경쟁도 {renderSortIcon("compIdx")}
                                </span>
                              </th>
                              <th scope="col" className="rank-keyword-th-num">
                                누적 블로그 발행 수
                              </th>
                              <th scope="col" className="rank-keyword-th-num">
                                블로그 포화도
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedHotKeywords.map((keyword, index) => {
                              const kw = keyword.relKeyword;
                              const blogTotal =
                                kw != null
                                  ? blogCountByKeyword[String(kw)]
                                  : undefined;
                              return (
                                <tr
                                  key={`${kw}-${
                                    (hotPage - 1) * HOT_PAGE_SIZE + index
                                  }`}
                                >
                                  <td className="rank-keyword-td-kw rank-keyword-td-sticky">
                                    {keyword.relKeyword}
                                  </td>
                                  <td className="rank-keyword-num rank-keyword-num-emphasis">
                                    {formatValue(totalSearchQc(keyword))}
                                  </td>
                                  <td className="rank-keyword-num">
                                    {formatValue(keyword.monthlyPcQcCnt)}
                                  </td>
                                  <td className="rank-keyword-num">
                                    {formatValue(keyword.monthlyMobileQcCnt)}
                                  </td>
                                  <td className="rank-keyword-num">
                                    {formatValue(keyword.monthlyAvePcClkCnt)}
                                  </td>
                                  <td className="rank-keyword-num">
                                    {formatValue(
                                      keyword.monthlyAveMobileClkCnt,
                                    )}
                                  </td>
                                  <td className="rank-keyword-num">
                                    {formatValue(keyword.monthlyAvePcCtr)}
                                  </td>
                                  <td className="rank-keyword-num">
                                    {formatValue(keyword.monthlyAveMobileCtr)}
                                  </td>
                                  <td className="rank-keyword-num">
                                    {formatValue(keyword.plAvgDepth)}
                                  </td>
                                  <td className="rank-keyword-comp-cell">
                                    <span
                                      className={compBadgeClass(keyword.compIdx)}
                                    >
                                      {keyword.compIdx || "-"}
                                    </span>
                                  </td>
                                  <td className="rank-keyword-num">
                                    {blogCountLoading
                                      ? "…"
                                      : blogTotal !== undefined
                                        ? formatValue(blogTotal)
                                        : "-"}
                                  </td>
                                  <td className="rank-keyword-num">
                                    {blogCountLoading
                                      ? "…"
                                      : formatBlogSaturation(
                                          blogTotal,
                                          keyword,
                                        )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {hotTotalPages > 0 && (
                        <div
                          className="rank-keyword-pager"
                          role="navigation"
                          aria-label="키워드 목록 페이지"
                        >
                          <button
                            type="button"
                            className="rank-keyword-pager-btn"
                            disabled={hotPage <= 1}
                            onClick={() =>
                              setHotPage((p) => Math.max(1, p - 1))
                            }
                          >
                            이전
                          </button>
                          <span className="rank-keyword-pager-info">
                            {hotPage} / {hotTotalPages}페이지 (총 {hotTotalCount}
                            개
                            {blogCountLoading ? " · 발행 수 불러오는 중…" : ""})
                          </span>
                          <button
                            type="button"
                            className="rank-keyword-pager-btn"
                            disabled={hotPage >= hotTotalPages}
                            onClick={() =>
                              setHotPage((p) =>
                                Math.min(hotTotalPages, p + 1),
                              )
                            }
                          >
                            다음
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

            {!hotLoading &&
              baseKeywords.length === 0 &&
              relatedKeywords.length === 0 &&
              hotSearched &&
              !hotError && (
                <div className="rank-keyword-hot-empty">
                  <p>검색 결과가 없습니다.</p>
                  <p>다른 키워드로 검색해보세요.</p>
                </div>
              )}

            {!hotLoading &&
              !hotSearched &&
              !hotError &&
              baseKeywords.length === 0 &&
              relatedKeywords.length === 0 && (
                <div className="rank-keyword-hot-empty">
                  <p>분석할 키워드를 입력해 검색해보세요.</p>
                </div>
              )}
          </div>
        </section>
      </div>

      <SimpleKeywordRankHistoryModal
        isOpen={user != null && simpleKeywordHistoryModal != null}
        onClose={() => setSimpleKeywordHistoryModal(null)}
        keywordId={simpleKeywordHistoryModal?.keywordId}
        keywordWord={simpleKeywordHistoryModal?.word}
      />

      <SimpleRankPlanUpgradeModal
        isOpen={user != null && planUpgradeModalOpen}
        onClose={() => setPlanUpgradeModalOpen(false)}
        onPurchaseSuccess={refreshAdRankPaymentAndProducts}
      />

      <SimpleProductRegisterModal
        isOpen={user != null && productRegisterModalOpen}
        onClose={() => setProductRegisterModalOpen(false)}
        onSuccess={refreshSimpleAdProducts}
        atProductLimit={
          simpleAdUsage.productCount >= adPaymentLimits.limitProduct
        }
        atKeywordLimit={
          simpleAdUsage.keywordCount >= adPaymentLimits.limitKeyword
        }
        keywordCount={simpleAdUsage.keywordCount}
        keywordLimit={adPaymentLimits.limitKeyword}
      />
    </div>
  );
};

export default RankKeyword;
